import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, Save, ArrowLeft, Info, Plus, Trash2,
  CheckCircle, XCircle, HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WithdrawalPolicySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [policy, setPolicy] = useState({
    seat_booking: { refundable: false, label: 'Seat Booking' },
    admission_fee: { refundable: true, label: 'Admission Fee' },
    semester_fee: { refundable: 'admin_decides', label: 'Semester/Tuition Fee' },
    caution_money: { refundable: true, label: 'Caution Money' },
    other_fee: { refundable: 'admin_decides', label: 'Other Fees' },
    default_deductions: [],
    require_reason: true,
    require_approval: false,
    notify_student: true,
    notify_accounts: true
  });

  useEffect(() => {
    fetchPolicy();
  }, []);

  const fetchPolicy = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/master-settings/withdrawal-policy`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPolicy(data);
      }
    } catch (error) {
      console.error('Error fetching policy:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/master-settings/withdrawal-policy`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(policy)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Withdrawal policy saved successfully!');
      } else {
        toast.error('Failed to save: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Failed to save policy: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateFeePolicy = (feeType, field, value) => {
    setPolicy(prev => ({
      ...prev,
      [feeType]: { ...prev[feeType], [field]: value }
    }));
  };

  const addDeduction = () => {
    setPolicy(prev => ({
      ...prev,
      default_deductions: [
        ...prev.default_deductions,
        { name: '', amount: 0, is_mandatory: false }
      ]
    }));
  };

  const updateDeduction = (index, field, value) => {
    setPolicy(prev => ({
      ...prev,
      default_deductions: prev.default_deductions.map((d, i) => 
        i === index ? { ...d, [field]: value } : d
      )
    }));
  };

  const removeDeduction = (index) => {
    setPolicy(prev => ({
      ...prev,
      default_deductions: prev.default_deductions.filter((_, i) => i !== index)
    }));
  };

  const RefundableOption = ({ value, onChange }) => (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
          value === true 
            ? 'bg-green-100 text-green-700 border-2 border-green-500' 
            : 'bg-gray-100 text-gray-500 border border-gray-200'
        }`}
      >
        <CheckCircle className="h-4 w-4" /> Yes
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
          value === false 
            ? 'bg-red-100 text-red-700 border-2 border-red-500' 
            : 'bg-gray-100 text-gray-500 border border-gray-200'
        }`}
      >
        <XCircle className="h-4 w-4" /> No
      </button>
      <button
        type="button"
        onClick={() => onChange('admin_decides')}
        className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 ${
          value === 'admin_decides' 
            ? 'bg-amber-100 text-amber-700 border-2 border-amber-500' 
            : 'bg-gray-100 text-gray-500 border border-gray-200'
        }`}
      >
        <HelpCircle className="h-4 w-4" /> Admin Decides
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Withdrawal Policy Settings</h1>
            <p className="text-gray-600 mt-1">Configure refund policy for student withdrawals</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Policy'}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How Refund Policy Works:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li><strong>Yes</strong> - Full amount is automatically refundable</li>
                <li><strong>No</strong> - Non-refundable (will not be shown in refund form)</li>
                <li><strong>Admin Decides</strong> - Admin can enter any amount up to the paid amount</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fee Refund Policy */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Fee Refund Policy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Seat Booking */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{policy.seat_booking?.label || 'Seat Booking'}</p>
                <p className="text-sm text-gray-500">Amount paid during seat reservation</p>
              </div>
              <RefundableOption 
                value={policy.seat_booking?.refundable} 
                onChange={(val) => updateFeePolicy('seat_booking', 'refundable', val)} 
              />
            </div>

            {/* Admission Fee */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{policy.admission_fee?.label || 'Admission Fee'}</p>
                <p className="text-sm text-gray-500">One-time admission/registration fee</p>
              </div>
              <RefundableOption 
                value={policy.admission_fee?.refundable} 
                onChange={(val) => updateFeePolicy('admission_fee', 'refundable', val)} 
              />
            </div>

            {/* Semester Fee */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{policy.semester_fee?.label || 'Semester/Tuition Fee'}</p>
                <p className="text-sm text-gray-500">Course fees paid per semester/term</p>
              </div>
              <RefundableOption 
                value={policy.semester_fee?.refundable} 
                onChange={(val) => updateFeePolicy('semester_fee', 'refundable', val)} 
              />
            </div>

            {/* Caution Money */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{policy.caution_money?.label || 'Caution Money'}</p>
                <p className="text-sm text-gray-500">Security deposit / refundable deposit</p>
              </div>
              <RefundableOption 
                value={policy.caution_money?.refundable} 
                onChange={(val) => updateFeePolicy('caution_money', 'refundable', val)} 
              />
            </div>

            {/* Other Fees */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium">{policy.other_fee?.label || 'Other Fees'}</p>
                <p className="text-sm text-gray-500">Lab fees, library fees, etc.</p>
              </div>
              <RefundableOption 
                value={policy.other_fee?.refundable} 
                onChange={(val) => updateFeePolicy('other_fee', 'refundable', val)} 
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Default Deductions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Default Deductions</span>
            <Button size="sm" variant="outline" onClick={addDeduction}>
              <Plus className="h-4 w-4 mr-1" /> Add Deduction
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            These deductions will be pre-filled when processing a withdrawal. Admin can modify them.
          </p>
          
          {policy.default_deductions?.length === 0 ? (
            <p className="text-center py-4 text-gray-400">No default deductions configured</p>
          ) : (
            <div className="space-y-3">
              {policy.default_deductions?.map((deduction, index) => (
                <div key={index} className="flex gap-3 items-center">
                  <input
                    type="text"
                    value={deduction.name}
                    onChange={(e) => updateDeduction(index, 'name', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Deduction Name (e.g., Processing Fee)"
                  />
                  <div className="flex items-center gap-1">
                    <span className="text-gray-500">₹</span>
                    <input
                      type="number"
                      value={deduction.amount}
                      onChange={(e) => updateDeduction(index, 'amount', parseFloat(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Amount"
                      min="0"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={deduction.is_mandatory}
                      onChange={(e) => updateDeduction(index, 'is_mandatory', e.target.checked)}
                      className="rounded"
                    />
                    Mandatory
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500"
                    onClick={() => removeDeduction(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_reason}
                onChange={(e) => setPolicy(prev => ({ ...prev, require_reason: e.target.checked }))}
                className="rounded"
              />
              <div>
                <p className="font-medium">Require Reason</p>
                <p className="text-sm text-gray-500">Withdrawal reason is mandatory</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={policy.require_approval}
                onChange={(e) => setPolicy(prev => ({ ...prev, require_approval: e.target.checked }))}
                className="rounded"
              />
              <div>
                <p className="font-medium">Require Approval</p>
                <p className="text-sm text-gray-500">Withdrawal needs approval from higher authority</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={policy.notify_student}
                onChange={(e) => setPolicy(prev => ({ ...prev, notify_student: e.target.checked }))}
                className="rounded"
              />
              <div>
                <p className="font-medium">Notify Student</p>
                <p className="text-sm text-gray-500">Send notification to student after withdrawal</p>
              </div>
            </label>
            
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={policy.notify_accounts}
                onChange={(e) => setPolicy(prev => ({ ...prev, notify_accounts: e.target.checked }))}
                className="rounded"
              />
              <div>
                <p className="font-medium">Notify Accounts</p>
                <p className="text-sm text-gray-500">Send notification to accounts department</p>
              </div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save Policy'}
        </Button>
      </div>
    </div>
  );
}
